"use client";

import { isDailyPuzzlePickerSlug } from "@/lib/daily-puzzle/picker";
import { DAILY_PUZZLE_PLAY_THRESHOLD_MS, recordDailyPuzzlePlay } from "@/lib/daily-puzzle/progress";
import { useEffect } from "react";

/**
 * After one minute of visible time on a Daily Puzzle picker game, mark that game complete for today.
 */
export function useDailyPuzzlePlayTimer(gameSlug: string): void {
  useEffect(() => {
    if (!isDailyPuzzlePickerSlug(gameSlug)) return;

    let remainingMs = DAILY_PUZZLE_PLAY_THRESHOLD_MS;
    let timeoutId: number | undefined;
    let startedAt = 0;

    const clearTimer = () => {
      if (timeoutId === undefined) return;
      window.clearTimeout(timeoutId);
      timeoutId = undefined;
    };

    const complete = () => {
      clearTimer();
      remainingMs = 0;
      recordDailyPuzzlePlay(gameSlug);
    };

    const startTimer = () => {
      if (remainingMs <= 0 || document.visibilityState === "hidden") return;
      startedAt = Date.now();
      timeoutId = window.setTimeout(complete, remainingMs);
    };

    const pauseTimer = () => {
      if (timeoutId === undefined) return;
      remainingMs = Math.max(0, remainingMs - (Date.now() - startedAt));
      clearTimer();
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        pauseTimer();
        return;
      }
      startTimer();
    };

    startTimer();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [gameSlug]);
}
